/**
 * Central fetch — ONE place `/next` talks to the backend, so there is one
 * token source and one error-handling path (ADR-0003 workstream 7: "central
 * auth/fetch, credentials:'same-origin', one token source").
 *
 * The live app's session model (server/db.py's `AuthSession`) is an
 * httponly, server-side session cookie — the browser never holds a bearer
 * token to manage. `credentials: 'same-origin'` is therefore the WHOLE
 * auth story on this side: the cookie rides along automatically, and this
 * file's job is just to make sure every call remembers to ask for it and to
 * normalize a 401 into one place (redirect-to-login) instead of every
 * screen reinventing that check. No credential of any kind is stored in
 * this module or in browser storage (project-wide rule: never localStorage/
 * sessionStorage for anything sensitive).
 */

export class ApiError extends Error {
  status: number;
  body: unknown;
  /** The host's request id for this response (Render's `rndr-id`, else `x-request-id`), when sent —
   * the one thing that lets support find the request in the logs. Null when there is none. */
  requestId: string | null;
  constructor(status: number, message: string, body: unknown, requestId: string | null = null) {
    super(message);
    this.status = status;
    this.body = body;
    this.requestId = requestId;
  }
}

export interface ApiFetchOptions extends Omit<RequestInit, "body"> {
  body?: unknown; // auto-JSON-encoded if not already a string/FormData
  onUnauthorized?: () => void;
}

const DEFAULT_UNAUTHORIZED = () => {
  if (typeof window === "undefined") return;
  // UI-009: a session that expires mid-flow used to dump the user at a bare /login and, after
  // signing in, at a blank intake — while their extract job kept running server-side. Carry where
  // they were so sign-in returns them to it.
  const here = window.location.pathname + window.location.search;
  const suffix = here && here !== "/login" ? `?next=${encodeURIComponent(here)}` : "";
  window.location.assign(`/login${suffix}`);
};

export async function apiFetch<T = unknown>(path: string, opts: ApiFetchOptions = {}): Promise<T> {
  const { body, onUnauthorized = DEFAULT_UNAUTHORIZED, headers, ...rest } = opts;

  const init: RequestInit = {
    ...rest,
    credentials: "same-origin", // the ONE token source — the httponly session cookie
    headers: {
      ...(body !== undefined && !(body instanceof FormData) ? { "Content-Type": "application/json" } : {}),
      ...headers,
    },
    body:
      body === undefined
        ? undefined
        : body instanceof FormData || typeof body === "string"
          ? (body as BodyInit)
          : JSON.stringify(body),
  };

  const res = await fetch(path, init);

  if (res.status === 401) {
    onUnauthorized();
    throw new ApiError(401, "Not authenticated", null);
  }

  const contentType = res.headers.get("content-type") || "";
  const parsed = contentType.includes("application/json") ? await res.json().catch(() => null) : await res.text();

  if (!res.ok) {
    const message =
      parsed && typeof parsed === "object" && "detail" in (parsed as any)
        ? String((parsed as any).detail)
        : `Request failed (${res.status})`;
    const rid = res.headers.get("rndr-id") || res.headers.get("x-request-id") || null;
    throw new ApiError(res.status, message, parsed, rid);
  }

  return parsed as T;
}
